import { Shield, Eye } from 'lucide-react'
import { Modal } from '@/components/ui'
import { cn } from '@/lib/utils.ts'
import type { TemplateStep } from './types.ts'
import { STEP_TYPE_META } from './stepTypeMeta.ts'

interface TemplatePreviewProps {
  open: boolean
  onClose: () => void
  templateName: string
  steps: TemplateStep[]
}

export function TemplatePreview({ open, onClose, templateName, steps }: TemplatePreviewProps) {
  const activeSteps = steps.filter((s) => s.config?.enabled !== false)

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-brand-600" />
          <span>Kundenansicht: {templateName}</span>
        </div>
      }
      description="Simulation der Kunden-Sicht. Nur aktive Schritte werden angezeigt."
      size="lg"
    >
      {activeSteps.length === 0 ? (
        <div className="py-8 text-center text-neutral-500 text-sm">
          Keine aktiven Schritte vorhanden.
        </div>
      ) : (
        <div className="space-y-2">
          {activeSteps.map((step, index) => {
            const meta = STEP_TYPE_META[step.step_type]
            const TypeIcon = meta.icon
            const isGate = step.step_type === 'approval'

            return (
              <div key={step.id} className="flex items-start gap-3">
                {/* Step number / connector */}
                <div className="flex flex-col items-center shrink-0">
                  {isGate ? (
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 border-2 border-amber-300">
                      <Shield className="w-4 h-4 text-amber-700" />
                    </div>
                  ) : (
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 border-2 border-brand-300 text-brand-700 text-sm font-semibold">
                      {index + 1}
                    </div>
                  )}
                  {index < activeSteps.length - 1 && (
                    <div className="w-0.5 h-4 bg-gray-200 my-0.5" />
                  )}
                </div>

                {/* Content */}
                <div
                  className={cn(
                    'flex-1 rounded-lg border p-3 mb-1',
                    isGate
                      ? 'bg-amber-50 border-amber-200'
                      : 'bg-white border-gray-200',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <TypeIcon className={cn('w-4 h-4 shrink-0', meta.textColor)} />
                    <span className="text-sm font-medium text-gray-900">{step.title}</span>
                    {step.is_required === 1 && (
                      <span className="text-red-500 text-xs" title="Pflichtschritt">*</span>
                    )}
                    <span
                      className={cn(
                        'ml-auto text-xs px-1.5 py-0.5 rounded font-medium',
                        meta.bgColor,
                        meta.textColor,
                      )}
                    >
                      {meta.label}
                    </span>
                  </div>
                  {step.description && (
                    <p className="mt-1 text-xs text-gray-500 ml-6">{step.description}</p>
                  )}
                  {isGate && step.config?.approval_role && (
                    <p className="mt-1 text-xs text-amber-700 ml-6">
                      Genehmiger: {step.config.approval_role}
                    </p>
                  )}
                  {step.config?.due_offset_days != null && (
                    <p className="mt-1 text-xs text-gray-400 ml-6">
                      Fällig: {step.config.due_offset_days} Tag
                      {step.config.due_offset_days !== 1 ? 'e' : ''} nach Start
                    </p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </Modal>
  )
}
